package com.solevo.dist.pages

import androidx.compose.runtime.*
import com.solevo.dist.components.PhoneNumberField
import com.solevo.dist.styles.UiStyles
import com.solevo.dist.components.dialogs.CountryDialog
import com.solevo.dist.navigation.Screen
import com.solevo.dist.theme.SLColors
import com.solevo.dist.theme.styles.h1
import com.solevo.dist.theme.styles.h2
import com.solevo.dist.theme.styles.textSelected
import com.solevo.dist.theme.styles.textUnselected
import com.solevo.dist.util.Res
import com.solevo.dist.viewmodels.LoginUiEvents
import com.solevo.dist.viewmodels.Mode
import com.solevo.dist.viewmodels.StartViewModel
import com.solevo.dist.viewmodels.Step
import com.solevo.dist.viewmodels.UiEffect
import com.solevo.libraries.entities.country.RemoteCountryService
import com.solevo.libraries.feature.authentication.ui.login.phoneNumber.LoginPhoneNumberEvents
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.margin
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.koin.compose.getKoin
import org.w3c.dom.HTMLInputElement

@Page
@Composable
fun Login() {
    Style(LoginStyles)
    val context = rememberPageContext()
    val koin = getKoin()
    val viewModel = remember {
        StartViewModel(
            loginWithPhoneNumber = koin.get(),
            remoteCountryService = koin.get(),
            authenticationSession = koin.get()
        ).also { it.initialize() }
    }

    val ui by viewModel.ui.collectAsState()
    val loginState by viewModel.state.collectAsState()
    var showCountryDialog by remember { mutableStateOf(false) }

    LaunchedEffect("countries-init") {
        viewModel.loadCountries()
    }

    LaunchedEffect(Unit) {
        viewModel.effects.collect { effect ->
            when (effect) {
                is UiEffect.ShowError -> console.log("ShowError: ${effect.message}")
                UiEffect.NavigateToNextStep -> {
                    context.router.navigateTo(Screen.Home.route)
                }
            }
        }
    }

    val scope = rememberCoroutineScope()
    Row(Modifier.fillMaxSize()) {
        LeftPane()
        ui.selectedCountry?.let { sc ->
            RightPane(
                step = ui.step,
                mode = ui.mode,
                phone = loginState.phoneNumberInputState.currentPhoneNumber,
                email = "",
                otp = ui.otp,
                onChangeStep = { step ->
                    scope.launch {
                        when (step) {
                            Step.OTP -> viewModel.handleEvent(LoginPhoneNumberEvents.ContinueButtonClicked)
                            Step.METHOD -> viewModel.handleUiEvents(LoginUiEvents.MethodStepUpdate)
                        }
                    }
                },
                onChangeMode = { mode ->
                    when (mode) {
                        Mode.PHONE -> viewModel.handleUiEvents(LoginUiEvents.ModePhoneUpdate)
                        Mode.EMAIL -> viewModel.handleUiEvents(LoginUiEvents.ModeEmailUpdate)
                    }
                },
                onChangePhone = { phoneNumber ->
                    scope.launch {
                        viewModel.handleEvent(LoginPhoneNumberEvents.PhoneNumberInputChanged(phoneNumber))
                    }
                },
                onChangeEmail = { /* email path if needed */ },
                onChangeOtp = { code -> viewModel.handleUiEvents(LoginUiEvents.OTPUpdate(code)) },
                onOtpContinue = { viewModel.handleUiEvents(LoginUiEvents.OTPSubmit) },
                onOtpResend = { viewModel.handleUiEvents(LoginUiEvents.OTPResend) },
                onClearOtpError = { viewModel.handleUiEvents(LoginUiEvents.ClearOtpError) },
                selectedCountry = sc,
                onCountryClick = { showCountryDialog = true },
                phoneErrorMessage = ui.phoneNumberError,
                isLoadingPhone = loginState.isLoadingInProgress,
                isLoadingOtp = loginState.isLoadingInProgress,
                otpError = ui.otpError,
                isResendingOtp = ui.isResending,
                resendCooldownSeconds = ui.resendCooldownSeconds,
                otpResent = ui.otpResent
            )
        } ?: run {
            Box(
                modifier = Modifier
                    .flexGrow(1)
                    .fillMaxHeight()
                    .backgroundColor(SLColors.White.rgb),
                contentAlignment = Alignment.Center
            ) {
                SpanText(LocalStrings.current.loading)
            }
        }
    }

    if (showCountryDialog) {
        CountryDialog(
            current = ui.selectedCountry,
            countries = ui.countries,
            onConfirm = { picked ->
                if (picked != null) viewModel.selectCountry(picked)
                showCountryDialog = false
            },
            onDismiss = { showCountryDialog = false }
        )
    }
}

//region COMPONENTS
//region LEFT PANE
@Composable
private fun LeftPane() {
    Box(
        modifier = Modifier
            .flexGrow(1)
            .fillMaxHeight()
            .backgroundColor(SLColors.LightGray.rgb)
            .classNames(LoginStyles.leftPaneResponsive),
        contentAlignment = Alignment.Center
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.gap(12.px)
        ) {
            Img(
                src = Res.Image.logo,
                attrs = Modifier
                    .size(96.px, 103.px)
                    .toAttrs { alt("Solevo Logo") }
            )
            SpanText(LocalStrings.current.appTitle, modifier = Modifier.h1())
        }
    }
}
//endregion

//region RIGHT PANE (router)
@Composable
private fun RightPane(
    step: Step,
    mode: Mode,
    phone: String,
    email: String,
    otp: String,
    onChangeStep: (Step) -> Unit,
    onChangeMode: (Mode) -> Unit,
    onChangePhone: (String) -> Unit,
    onChangeEmail: (String) -> Unit,
    onChangeOtp: (String) -> Unit,
    onOtpContinue: () -> Unit,
    onOtpResend: () -> Unit,
    onClearOtpError: () -> Unit,
    selectedCountry: RemoteCountryService.CountryPayload.PhoneCallingCode,
    onCountryClick: () -> Unit,
    phoneErrorMessage: String?,
    isLoadingPhone: Boolean,
    isLoadingOtp: Boolean,
    otpError: String?,
    isResendingOtp: Boolean,
    resendCooldownSeconds: Int,
    otpResent: Boolean
) {
    Box(
        modifier = Modifier
            .flexGrow(1)
            .fillMaxHeight()
            .backgroundColor(SLColors.White.rgb),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .width(320.px)
                .gap(12.px)
                .alignItems(AlignItems.FlexStart)
                .classNames(LoginStyles.formResponsive)
        ) {
            when (step) {
                Step.METHOD -> MethodForm(
                    mode = mode,
                    phone = phone,
                    email = email,
                    onChangeMode = onChangeMode,
                    onChangePhone = onChangePhone,
                    onChangeEmail = onChangeEmail,
                    onSendCode = { onChangeStep(Step.OTP) },
                    selectedCountry = selectedCountry,
                    onCountryClick = onCountryClick,
                    phoneErrorMessage = phoneErrorMessage,
                    isLoadingPhone = isLoadingPhone
                )

                Step.OTP -> OtpForm(
                    otp = otp,
                    onBack = { onChangeStep(Step.METHOD) },
                    onChangeOtp = onChangeOtp,
                    onContinue = onOtpContinue,
                    onResend = onOtpResend,
                    isLoading = isLoadingOtp,
                    isResending = isResendingOtp,
                    resendCooldownSeconds = resendCooldownSeconds,
                    otpResent = otpResent,
                    error = otpError,
                    onClearError = onClearOtpError
                )
            }
        }
    }
}
//endregion

//region METHOD FORM (Phone / Email)
@Composable
private fun MethodForm(
    mode: Mode,
    phone: String,
    email: String,
    onChangeMode: (Mode) -> Unit,
    onChangePhone: (String) -> Unit,
    onChangeEmail: (String) -> Unit,
    onSendCode: () -> Unit,
    selectedCountry: RemoteCountryService.CountryPayload.PhoneCallingCode,
    onCountryClick: () -> Unit,
    phoneErrorMessage: String?,
    isLoadingPhone: Boolean
) {
    val strings = LocalStrings.current
    SpanText(strings.login, modifier = Modifier.h2().margin(bottom = 4.px))

    Row(modifier = Modifier.width(320.px).gap(40.px)) {
        TabItem(strings.loginPhone, selected = mode == Mode.PHONE) { onChangeMode(Mode.PHONE) }
        // TabItem(strings.loginEmail, selected = mode == Mode.EMAIL) { onChangeMode(Mode.EMAIL) }
    }

    if (mode == Mode.PHONE) {
        Label(
            attrs = UiStyles.Labels.label.toModifier().toAttrs()
        ) { Text(strings.loginEnterPhone) }

        PhoneNumberField(
            selectedCountry = selectedCountry,
            phoneValue = phone,
            onPhoneChange = onChangePhone,
            onCountryClick = onCountryClick,
            placeholder = strings.loginPhonePlaceholder
        )

        phoneErrorMessage?.let { SpanText(it, modifier = Modifier.color(SLColors.Red.rgb)) }

    } else {
        Label(attrs = { UiStyles.Labels.label }) { Text(strings.loginEnterEmail) }
        Input(InputType.Email) {
            classes(LoginStyles.input)
            value(email)
            onInput { onChangeEmail(it.value) }
            attr("placeholder", strings.loginEmailPlaceholder)
        }
        SmallHint(strings.loginEmailAccepted)
    }

    if (isLoadingPhone) {
        Box(
            modifier = Modifier
                .width(320.px)
                .padding(leftRight = 12.px, topBottom = 14.px)
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .justifyContent(JustifyContent.Center)
        ) {
            SpanText(strings.loading, Modifier.color(rgb(120, 124, 130)))
        }
    } else {
        Button(
            onClick = { onSendCode() },
            enabled = if (mode == Mode.PHONE) phone.isNotBlank() else email.isNotBlank(),
            modifier = UiStyles.Buttons.primaryButton.toModifier()
        ) { SpanText(strings.sendCode) }
    }
}
//endregion

//region OTP FORM
@Composable
private fun OtpForm(
    otp: String,
    onBack: () -> Unit,
    onChangeOtp: (String) -> Unit,
    onContinue: () -> Unit,
    onResend: () -> Unit,
    isLoading: Boolean,
    isResending: Boolean,
    resendCooldownSeconds: Int,
    otpResent: Boolean,
    error: String?,
    onClearError: () -> Unit
) {
    val strings = LocalStrings.current
    var otpInputRef by remember { mutableStateOf<HTMLInputElement?>(null) }
    LaunchedEffect(Unit) { otpInputRef?.focus() }
    
    if (error != null) {
        com.solevo.dist.components.dialogs.ErrorDialog(
            title = strings.errorPrefix,
            message = error,
            buttonText = strings.close,
            onDismiss = onClearError
        )
    }

    A(href = "#", attrs = {
        classes(LoginStyles.backLink)
        onClick { onBack() }
    }) { Text(strings.loginStepBack) }

    SpanText(
        strings.loginEnterCode,
        modifier = Modifier
            .fontSize(20.px)
            .fontWeight(FontWeight.SemiBold)
            .color(rgb(31, 33, 37))
    )
    SpanText(
        strings.loginCodeDescription,
        modifier = Modifier
            .fontSize(14.px)
            .color(rgb(120, 124, 130))
            .margin(bottom = 8.px)
    )

    Box(
        modifier = Modifier
            .position(Position.Relative)
            .margin(top = 4.px, bottom = 4.px)
    ) {
        Row(modifier = Modifier.gap(10.px)) {
            repeat(6) { idx ->
                val ch = otp.getOrNull(idx) ?: ' '
                Box(
                    modifier = Modifier
                        .size(40.px, 48.px)
                        .borderRadius(10.px)
                        .border(1.px, LineStyle.Solid, if (ch == ' ') rgb(176, 182, 192) else rgb(30, 100, 255))
                        .backgroundColor(Colors.White)
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .justifyContent(JustifyContent.Center)
                ) {
                    SpanText(
                        ch.toString(),
                        modifier = Modifier.fontSize(18.px).fontWeight(FontWeight.Bold).color(rgb(31, 33, 37))
                    )
                }
            }
        }

        // hidden input
        Input(InputType.Text) {
            classes(LoginStyles.otpHiddenInput)
            ref { el ->
                otpInputRef = el
                onDispose { otpInputRef = null }
            }
            value(otp)
            attr("inputmode", "numeric")
            attr("autocomplete", "one-time-code")
            attr("pattern", "[0-9]*")
            onInput { ev -> onChangeOtp(ev.value.filter { it.isDigit() }.take(6)) }
        }

        Div({
            style {
                position(Position.Absolute)
                left(0.px); top(0.px)
                width(100.percent); height(100.percent)
            }
            onClick { otpInputRef?.focus() }
        })
    }

    if (otpResent) {
        SpanText(
            strings.otpResent,
            modifier = Modifier.fontSize(13.px).color(rgb(22, 140, 80))
        )
    }

    val canResend = !isResending && !isLoading && resendCooldownSeconds == 0
    Button(
        onClick = { onResend() },
        enabled = canResend,
        modifier = Modifier
            .color(if (canResend) rgb(30, 100, 255) else rgb(150, 154, 160))
            .backgroundColor(Colors.Transparent)
            .fontWeight(FontWeight.SemiBold)
            .margin(top = 8.px, bottom = 4.px)
    ) {
        SpanText(
            when {
                isResending -> strings.loading
                resendCooldownSeconds > 0 -> strings.resendCodeIn.replace("{seconds}", resendCooldownSeconds.toString())
                else -> strings.resendCode
            }
        )
    }

    if (isLoading) {
        Box(
            modifier = Modifier
                .width(320.px)
                .padding(leftRight = 12.px, topBottom = 14.px)
                .display(DisplayStyle.Flex)
                .alignItems(AlignItems.Center)
                .justifyContent(JustifyContent.Center)
        ) {
            SpanText(strings.loading, Modifier.color(rgb(120, 124, 130)))
        }
    } else {
        Button(
            onClick = { onContinue() },
            enabled = otp.length == 6,
            modifier = UiStyles.Buttons.primaryButton.toModifier()
        ) { SpanText(strings.cont) }
    }
}
//endregion

//region TABS & HINT
@Composable
private fun TabItem(label: String, selected: Boolean, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .padding(bottom = 6.px)
            .maxWidth(150.px),
        horizontalAlignment = Alignment.Start
    ) {
        SpanText(
            label,
            modifier = Modifier
                .then(if (selected) Modifier.textSelected() else Modifier.textUnselected())
                .onClick { onClick() }
        )
        Box(
            modifier = Modifier
                .align(Alignment.Start)
                .padding(leftRight = 8.px)
                .height(2.px)
                .backgroundColor(if (selected) SLColors.DarkGray.rgb else SLColors.LightGrayUltra.rgb)
                .fillMaxWidth()
                .styleModifier { property("min-width", "calc(100% + 20px)") }
        )
    }
}

@Composable
private fun SmallHint(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .fontSize(12.px)
            .color(com.solevo.dist.theme.textPrimaryColor)
            .margin(bottom = 16.px)
    )
}
//endregion

//region STYLES
private object LoginStyles : StyleSheet() {
    val input by style {
        width(320.px)
        padding(12.px, 14.px)
        fontSize(14.px)
        borderRadius(10.px)
        border(1.px, LineStyle.Solid, rgb(223, 226, 231))
        outline("none")
        marginBottom(8.px)
        backgroundColor(Color.white)
    }

    val otpHiddenInput by style {
        position(Position.Absolute)
        left(0.px)
        top(0.px)
        width(1.px)
        height(1.px)
        opacity(0)
        property("caret-color", "transparent")
    }

    val backLink by style {
        color(rgb(120, 124, 130))
        textDecoration("none")
        fontSize(13.px)
        property("cursor", "pointer")
        margin(bottom = 8.px)
    }

    val leftPaneResponsive by style {
        media(mediaMaxWidth(900.px)) {
            self { display(DisplayStyle.None) }
        }
    }

    val formResponsive by style {
        media(mediaMaxWidth(900.px)) {
            self {
                width(90.percent)
                maxWidth(420.px)
            }
        }
    }
}
//endregion